/*
 * Automations (doc 04 §5, docs/13 §2.6): a trigger, then steps (message, wait, check). One run per customer
 * per trigger lives in automation_runs; the state machine here decides, from the run's position and a few
 * facts, what happens next. Pure: the Worker gathers the facts, applies the action, stores the position.
 * The ten starters are defined here too, switched off, with placeholder wording for Saif.
 */
import type { EventType } from '../events.js';
import { parseHHMM, wallClock, addDays, fromWallClock } from '../time.js';
import { nextSendTime, type SendTimeOptions } from './rules.js';
import type { ChannelContent, MarketingChannel } from './types.js';

export type TraitTrigger = 'lapsing' | 'credit_unused' | 'browsed_not_ordered' | 'try_new_flavour' | 'catering_no_repeat';
export const TRAIT_TRIGGERS: TraitTrigger[] = ['lapsing', 'credit_unused', 'browsed_not_ordered', 'try_new_flavour', 'catering_no_repeat'];

export type Trigger =
  | { type: 'event'; event: EventType; where?: { key: string; equals?: string; notEquals?: string }; /** points_earned only: fire when the balance crosses a reward tier */ crossesTier?: boolean }
  | { type: 'trait'; trait: TraitTrigger }
  | { type: 'manual' };

export interface WaitSpec {
  minutes?: number;
  hours?: number;
  days?: number;
  /** After the delay, land in the lunchtime window, or the next morning (quiet hours end). */
  at?: 'lunchtime' | 'morning';
  /** If the trigger happened at or after this London time, skip the delay and use `at` (default lunchtime) instead: "after 30 min, or next lunchtime if late". */
  lateAfter?: string;
}
export type CheckTest = 'no_order_since_trigger' | 'reward_unused' | 'rating_high' | 'no_catering_repeat';
export const CHECK_TESTS: CheckTest[] = ['no_order_since_trigger', 'reward_unused', 'rating_high', 'no_catering_repeat'];
export const CHECK_LABELS: Record<CheckTest, string> = { no_order_since_trigger: 'Still no order since the trigger', reward_unused: 'The reward is still unused', rating_high: 'They gave 4 or 5 stars', no_catering_repeat: 'No catering order since' };

export type StepDef =
  | { kind: 'message'; channels: MarketingChannel[]; content: ChannelContent; code?: string | null; link?: string | null }
  | { kind: 'wait'; wait: WaitSpec }
  | { kind: 'check'; check: CheckTest };

export interface AutomationDef {
  key: string;
  name: string;
  description: string;
  trigger: Trigger;
  steps: StepDef[];
  cooldownDays: number;
  oncePerCustomer: boolean;
  stopOnOrder: boolean;
}

export interface RunState {
  /** The next step to run. */
  stepIndex: number;
  startedAt: string;
}
export interface RunFacts {
  orderedSinceTrigger: boolean;
  checks: Partial<Record<CheckTest, boolean>>;
}
export type RunAction =
  | { type: 'send'; stepIndex: number; step: Extract<StepDef, { kind: 'message' }>; thenIndex: number }
  | { type: 'wait'; until: Date; thenIndex: number }
  | { type: 'stop'; outcome: 'completed' | 'ordered' | 'check_failed' };

/** When a wait step ends, from the moment it starts. */
export function waitUntil(from: Date, wait: WaitSpec, timing: SendTimeOptions): Date {
  const late = wait.lateAfter !== undefined && wallClock(from).minutes >= parseHHMM(wait.lateAfter);
  let t = late ? from : new Date(from.getTime() + ((wait.minutes ?? 0) * 60_000 + (wait.hours ?? 0) * 3600_000 + (wait.days ?? 0) * 86400_000));
  const at = late ? (wait.at ?? 'lunchtime') : wait.at;
  if (at === 'lunchtime') t = nextSendTime(t, { ...timing, window: true });
  else if (at === 'morning') {
    // The next quiet-hours end strictly after t, on an opening day.
    const [h, m] = timing.settings.quietHours.to.split(':').map(Number) as [number, number];
    const w = wallClock(t);
    const today = fromWallClock(w.dateKey, h, m);
    t = nextSendTime(today.getTime() > t.getTime() ? today : fromWallClock(addDays(w.dateKey, 1), h, m), { ...timing, window: false });
  } else t = nextSendTime(t, { ...timing, window: false });
  return t;
}

/**
 * What a run does now. The caller applies the action and calls again until it gets 'wait' or 'stop': after a
 * message the following wait is set in the same tick, so a two-step journey never waits a minute between them.
 */
export function advanceRun(def: Pick<AutomationDef, 'steps' | 'stopOnOrder'>, run: RunState, facts: RunFacts, now: Date, timing: SendTimeOptions): RunAction {
  if (def.stopOnOrder && facts.orderedSinceTrigger) return { type: 'stop', outcome: 'ordered' };
  for (let i = run.stepIndex; i < def.steps.length; i++) {
    const step = def.steps[i]!;
    if (step.kind === 'check') {
      if (facts.checks[step.check] === false) return { type: 'stop', outcome: 'check_failed' };
      continue;
    }
    if (step.kind === 'wait') {
      const until = waitUntil(now, step.wait, timing);
      if (until.getTime() > now.getTime()) return { type: 'wait', until, thenIndex: i + 1 };
      continue;
    }
    return { type: 'send', stepIndex: i, step, thenIndex: i + 1 };
  }
  return { type: 'stop', outcome: 'completed' };
}

export class AutomationError extends Error {
  override name = 'AutomationError';
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown, label: string, max: number): number | undefined => {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > max) throw new AutomationError(`${label} must be between 0 and ${max}`);
  return v;
};

/** Shape check for steps from the admin; message wording is checked by the API's content validator. */
export function validateStepShape(raw: unknown, i: number): StepDef {
  if (!isObj(raw)) throw new AutomationError(`Step ${i + 1} is not valid`);
  switch (raw.kind) {
    case 'wait': {
      const w = isObj(raw.wait) ? raw.wait : {};
      const minutes = num(w.minutes, `Step ${i + 1}: minutes`, 24 * 60), hours = num(w.hours, `Step ${i + 1}: hours`, 24 * 14), days = num(w.days, `Step ${i + 1}: days`, 365);
      const at = w.at === 'lunchtime' || w.at === 'morning' ? w.at : undefined;
      let lateAfter: string | undefined;
      if (typeof w.lateAfter === 'string' && w.lateAfter) {
        try {
          parseHHMM(w.lateAfter);
        } catch {
          throw new AutomationError(`Step ${i + 1}: "late after" must be a time like 18:00`);
        }
        lateAfter = w.lateAfter;
      }
      if (!minutes && !hours && !days && !at) throw new AutomationError(`Step ${i + 1}: a wait needs a delay or a time of day`);
      return { kind: 'wait', wait: { ...(minutes !== undefined ? { minutes } : {}), ...(hours !== undefined ? { hours } : {}), ...(days !== undefined ? { days } : {}), ...(at ? { at } : {}), ...(lateAfter ? { lateAfter } : {}) } };
    }
    case 'check':
      if (!CHECK_TESTS.includes(raw.check as CheckTest)) throw new AutomationError(`Step ${i + 1}: unknown check`);
      return { kind: 'check', check: raw.check as CheckTest };
    case 'message': {
      const channels = Array.isArray(raw.channels) ? raw.channels.filter((c): c is MarketingChannel => c === 'push' || c === 'in_app' || c === 'email' || c === 'sms') : [];
      if (!channels.length) throw new AutomationError(`Step ${i + 1}: a message needs at least one channel`);
      return { kind: 'message', channels, content: (isObj(raw.content) ? raw.content : {}) as ChannelContent, code: typeof raw.code === 'string' && raw.code ? raw.code.toUpperCase() : null, link: typeof raw.link === 'string' && raw.link ? raw.link : null };
    }
    default: throw new AutomationError(`Step ${i + 1}: kind must be message, wait or check`);
  }
}

export function validateTrigger(raw: unknown): Trigger {
  if (!isObj(raw)) throw new AutomationError('The trigger is missing');
  if (raw.type === 'manual') return { type: 'manual' };
  if (raw.type === 'trait') {
    if (!TRAIT_TRIGGERS.includes(raw.trait as TraitTrigger)) throw new AutomationError(`Unknown trait trigger; use one of ${TRAIT_TRIGGERS.join(', ')}`);
    return { type: 'trait', trait: raw.trait as TraitTrigger };
  }
  if (raw.type === 'event') {
    if (typeof raw.event !== 'string' || !raw.event) throw new AutomationError('The event trigger needs an event');
    const t: Trigger = { type: 'event', event: raw.event as EventType };
    if (isObj(raw.where) && typeof raw.where.key === 'string') t.where = { key: raw.where.key, ...(typeof raw.where.equals === 'string' ? { equals: raw.where.equals } : {}), ...(typeof raw.where.notEquals === 'string' ? { notEquals: raw.where.notEquals } : {}) };
    if (raw.crossesTier === true) t.crossesTier = true;
    return t;
  }
  throw new AutomationError('The trigger type must be event, trait or manual');
}

export function describeTrigger(t: Trigger): string {
  if (t.type === 'manual') return 'Started by hand (a template send)';
  if (t.type === 'trait') return { lapsing: 'Order gap past twice their usual rhythm', credit_unused: 'Credit of £5+ and no order in 14 days', browsed_not_ordered: 'Viewed an item 2+ times in 7 days, no order', try_new_flavour: 'Same flavour ordered 3+ times', catering_no_repeat: 'No catering order for 30 days' }[t.trait];
  const base = t.event.replace(/_/g, ' ');
  if (t.crossesTier) return 'Points cross a reward tier';
  if (t.where?.equals) return `${base} (${t.where.key} = ${t.where.equals})`;
  if (t.where?.notEquals) return `${base} (not ${t.where.notEquals})`;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function describeWait(w: WaitSpec): string {
  const parts: string[] = [];
  if (w.days) parts.push(`${w.days} day${w.days === 1 ? '' : 's'}`);
  if (w.hours) parts.push(`${w.hours} hour${w.hours === 1 ? '' : 's'}`);
  if (w.minutes) parts.push(`${w.minutes} min`);
  let s = parts.length ? `Wait ${parts.join(' ')}` : 'Wait';
  if (w.at === 'lunchtime') s += parts.length ? ', then the next lunchtime' : ' for the next lunchtime';
  if (w.at === 'morning') s += parts.length ? ', then the next morning' : ' for the next morning';
  if (w.lateAfter) s += ` (or the next ${w.at ?? 'lunchtime'} if it is after ${w.lateAfter})`;
  return s;
}

/* ---------- the ten starters (doc 04 §5; wording is a placeholder Saif approves in the admin) ---------- */

const push = (title: string, body: string) => ({ push: { title, body } });
const card = (title: string, body: string, cta?: string) => ({ in_app: { title, body, ...(cta ? { cta } : {}) } });
const email = (subject: string, body: string, preheader?: string) => ({ email: { subject, body, ...(preheader ? { preheader } : {}) } });
const msg = (channels: MarketingChannel[], content: ChannelContent, link?: string): StepDef => ({ kind: 'message', channels, content, link: link ?? null, code: null });

export const STARTER_AUTOMATIONS: AutomationDef[] = [
  {
    key: 'welcome', name: 'Welcome series', description: 'Sign-up → "Your 100 points are waiting" at once (in-app + push); no order in 2 days → lunchtime nudge with the menu; still none at 7 days → one email.',
    trigger: { type: 'event', event: 'signed_up' }, cooldownDays: 365, oncePerCustomer: true, stopOnOrder: true,
    steps: [
      msg(['in_app', 'push'], { ...card('Your 100 points are waiting', 'Welcome to the Rewards Club, {first_name|friend}. 250 points gets a free side, and 5 points land for every £1.', 'See the menu'), ...push('Your 100 points are waiting', '250 gets a free side. Lunch?') }, '/menu'),
      { kind: 'wait', wait: { days: 2, at: 'lunchtime' } },
      msg(['push', 'in_app'], { ...push('Lunch sorted in 60 seconds', 'Chicken or paneer tikka, your way. Order ahead and skip the queue.'), ...card('Lunch sorted in 60 seconds', 'Five flavours, four formats. Order ahead and collect at the counter.', 'Start an order') }, '/menu'),
      { kind: 'wait', wait: { days: 5, at: 'lunchtime' } },
      msg(['email'], email('Your free side is 150 points away', '# Welcome to Tikkas\n\nHi {first_name|there},\n\nYou joined the Rewards Club with 100 points. Your first order takes you most of the way to a free side or dessert.\n\nWe are a short walk from Oxford Circus at 1 Great Titchfield Street. Order ahead in the app and your bowl is bagged with your name on it.', 'A first-order nudge'), '/menu'),
    ],
  },
  {
    key: 'abandoned_bag', name: 'Abandoned bag', description: 'A bag left without ordering → after 30 minutes (or the next lunchtime if late) "Your bowl\'s still in your bag", which opens the bag. Once per customer.',
    trigger: { type: 'event', event: 'bag_abandoned' }, cooldownDays: 365, oncePerCustomer: true, stopOnOrder: true,
    steps: [
      { kind: 'wait', wait: { minutes: 30, lateAfter: '18:00' } },
      msg(['push', 'in_app'], { ...push("Your bowl's still in your bag", 'Finish your order in one tap.'), ...card("Your bowl's still in your bag", 'Your {usual_order|bowl} is waiting. One tap to finish.', 'Open my bag') }, '/bag'),
    ],
  },
  {
    key: 'browsed', name: "Browsed, didn't order", description: 'Viewed an item 2+ times in a week without ordering → next lunchtime "Fancy the Lassuni today?"',
    trigger: { type: 'trait', trait: 'browsed_not_ordered' }, cooldownDays: 14, oncePerCustomer: false, stopOnOrder: true,
    steps: [
      { kind: 'wait', wait: { at: 'lunchtime' } },
      msg(['push', 'in_app'], { ...push('Fancy the {item|Rice Bowl} today?', 'You had a look. Here it is, ready in about 12 minutes.'), ...card('Fancy the {item|Rice Bowl} today?', 'You had a look earlier. Order ahead and it is ready in about 12 minutes.', 'Order it') }, '/menu'),
    ],
  },
  {
    key: 'try_new', name: 'Try something new', description: 'Same flavour ordered 3+ times → suggest one they have not tried, matched on heat (Classic → Malai, Angara → Lassuni). Attach a bonus-points code if you like.',
    trigger: { type: 'trait', trait: 'try_new_flavour' }, cooldownDays: 60, oncePerCustomer: false, stopOnOrder: true,
    steps: [
      { kind: 'wait', wait: { at: 'lunchtime' } },
      msg(['push', 'in_app'], { ...push('Loyal to {favourite_flavour}? Meet {new_flavour}', 'Same heat, new flavour. Give it a go today.'), ...card('Loyal to {favourite_flavour}? Meet {new_flavour}', 'Three {favourite_flavour} bowls says you know what you like. {new_flavour} sits at the same heat. Give it a go.', 'Try {new_flavour}') }, '/menu'),
    ],
  },
  {
    key: 'lapsing', name: 'Lapsing regular', description: 'The order gap goes past twice their usual rhythm → "We\'ve missed you" with a win-back code from Offers and codes; then an email; a text only if you add it.',
    trigger: { type: 'trait', trait: 'lapsing' }, cooldownDays: 30, oncePerCustomer: false, stopOnOrder: true,
    steps: [
      { kind: 'wait', wait: { at: 'lunchtime' } },
      msg(['push', 'in_app'], { ...push("We've missed you, {first_name|friend}", 'Your {usual_order|usual} is still here. Lunch on us a little: see the app.'), ...card("We've missed you", 'Your {usual_order|usual} is still here. Here is a little something for your next one.', 'Order again') }, '/menu'),
      { kind: 'wait', wait: { days: 3, at: 'lunchtime' } },
      msg(['email'], email("We've kept your usual warm", '# We have missed you\n\nHi {first_name|there},\n\nIt has been a while. Your {usual_order|usual order} is still on the menu, and the grill is on from 11.\n\nSee you at the counter.'), '/menu'),
    ],
  },
  {
    key: 'reward_ready', name: 'Reward ready', description: 'Points cross a reward tier → "You\'ve got a free side waiting"; a reminder after 14 days if unused.',
    trigger: { type: 'event', event: 'points_earned', crossesTier: true }, cooldownDays: 7, oncePerCustomer: false, stopOnOrder: false,
    steps: [
      msg(['push', 'in_app'], { ...push("You've got {reward|a free item} waiting", 'Add it to your next order from Rewards.'), ...card("You've got {reward|a free item} waiting", '{points} points. Add it to your next order from Rewards.', 'See rewards') }, '/rewards'),
      { kind: 'wait', wait: { days: 14, at: 'lunchtime' } },
      { kind: 'check', check: 'reward_unused' },
      msg(['push', 'in_app'], { ...push('Still got {reward|a free item} waiting', 'Your points are sitting there. Lunch?'), ...card('Still got {reward|a free item} waiting', 'Your points are sitting there. Use them on your next order.', 'See rewards') }, '/rewards'),
    ],
  },
  {
    key: 'credit_unused', name: 'Credit sitting unused', description: 'Store credit of £5+ and no order in 14 days → one gentle reminder.',
    trigger: { type: 'trait', trait: 'credit_unused' }, cooldownDays: 30, oncePerCustomer: false, stopOnOrder: true,
    steps: [
      { kind: 'wait', wait: { at: 'lunchtime' } },
      msg(['push', 'in_app'], { ...push("You've got {credit} of credit", 'It never expires, and it earns double points. Lunch?'), ...card("You've got {credit} of credit", 'Store credit never expires and earns 10 points per £1. Use it on your next order.', 'Order now') }, '/menu'),
    ],
  },
  {
    key: 'after_order', name: 'After the order', description: '90 minutes after collection → "How was it?" (a rating in the app). 4–5 stars → a referral nudge: "Give a friend free Tikki Tots".',
    trigger: { type: 'event', event: 'order_collected', where: { key: 'channel', notEquals: 'catering' } }, cooldownDays: 7, oncePerCustomer: false, stopOnOrder: false,
    steps: [
      { kind: 'wait', wait: { minutes: 90 } },
      msg(['in_app', 'push'], { ...card('How was it?', 'Order #{order_number}: tap a star. It takes two seconds and the kitchen reads every one.', 'Rate it'), ...push('How was it?', 'Order #{order_number}: tap a star.') }, '/account?rate=1'),
      { kind: 'wait', wait: { days: 1, at: 'lunchtime' } },
      { kind: 'check', check: 'rating_high' },
      msg(['in_app', 'push'], { ...card('Give a friend free Tikki Tots', 'Glad you liked it. Share your code and you both get free Tikki Tots.', 'Invite a friend'), ...push('Give a friend free Tikki Tots', 'Share your code: you both get free Tikki Tots.') }, '/invite'),
    ],
  },
  {
    key: 'new_flavour', name: 'New flavour drop', description: 'A template: the Founders Club gets the news 24 hours before everyone else. Start it from Sends with the "founder" group segment, then everyone the next day.',
    trigger: { type: 'manual' }, cooldownDays: 1, oncePerCustomer: false, stopOnOrder: false,
    steps: [
      msg(['push', 'in_app', 'email'], { ...push('New flavour: {new_flavour|see the app}', 'Founders Club first. Everyone else tomorrow.'), ...card('New flavour drop', 'Founders Club gets it first: a new flavour is on the grill today. Everyone else sees it tomorrow.', 'See it'), ...email('New flavour, Founders first', '# A new flavour\n\nHi {first_name|there},\n\nFounders Club gets it a day early. It is on the grill from 11 today.', 'Founders Club early access') }, '/menu'),
    ],
  },
  {
    key: 'catering', name: 'Catering follow-up', description: 'After a catering order → a follow-up email the next morning with one-click reorder; no repeat in 30 days → "Same again for the team?" (stage 4 data; dormant until then).',
    trigger: { type: 'event', event: 'order_collected', where: { key: 'channel', equals: 'catering' } }, cooldownDays: 30, oncePerCustomer: false, stopOnOrder: false,
    steps: [
      { kind: 'wait', wait: { at: 'morning' } },
      msg(['email'], email('How did the team get on?', '# Thanks for ordering for the team\n\nHi {first_name|there},\n\nWe hope lunch went down well. Same again is one tap away in the app, and the invoice is in your account.', 'Reorder in one tap'), '/catering'),
      { kind: 'wait', wait: { days: 30, at: 'morning' } },
      { kind: 'check', check: 'no_catering_repeat' },
      msg(['email'], email('Same again for the team?', '# Same again for the team?\n\nHi {first_name|there},\n\nIt has been a month. Your last catering order is saved: reorder it in one tap, or build a new one.'), '/catering'),
    ],
  },
];
